"use client";

import Link from "next/link";
import { useEffect } from "react";

import Header from "@/components/Home/Header";
import Hero from "@/components/Home/Hero";
import CardPayments from "@/components/Home/CardPayments";
import PartnerProgram from "@/components/Home/PartnerProgram";
import Footer from "@/components/Home/Footer";
import JsonLd from "@/components/Home/JsonLd";
import { FAQ } from "@/components/Home/faqData";
import { COMPANY_MSB_REGISTRATION } from "@/content/company";

const METHOD_COINS = [
  { icon: "/Images/landing/coin-monero.svg", name: "XMR" },
  { icon: "/Images/landing/coin-usdc.svg", name: "USDC" },
  { icon: "/Images/landing/coin-usdt.svg", name: "USDT" },
  { icon: "/Images/landing/coin-btc.svg", name: "BTC" },
  { icon: "/Images/landing/coin-eth.svg", name: "ETH" },
  { icon: "/Images/landing/coin-ltc.svg", name: "LTC" },
  { icon: "/Images/landing/coin-bnb.svg", name: "BNB" },
  { icon: "/Images/landing/coin-ton.svg", name: "GRAM" },
];

export default function HomeComponent() {
  useEffect(() => {
    const section = document.querySelectorAll(".scroll");
    const onEntry = (entry: IntersectionObserverEntry[]) =>
      entry.forEach((change) => {
        if (change.isIntersecting)
          change.target.classList.add("scroll--active");
      });

    let currThreshold = 0.3;
    if (window.innerWidth <= 768) currThreshold = 0.1;

    const observer = new IntersectionObserver(onEntry, {
      threshold: [currThreshold],
    });
    for (const elm of section) observer.observe(elm);

    const accordions = (
      accBtn: NodeListOf<Element>,
      accContent: NodeListOf<Element>,
      accHead: NodeListOf<Element>,
    ) => {
      accBtn.forEach((item, i) => {
        item.addEventListener("click", () => {
          accContent[i].classList.toggle("faq-open");
          accHead[i].classList.toggle("faq-head-open");

          if ((accContent[i] as HTMLElement).classList.contains("faq-open"))
            (accContent[i] as HTMLElement).style.maxHeight =
              (accContent[i] as HTMLElement).scrollHeight + "px";
          else (accContent[i] as HTMLElement).style.maxHeight = "";
        });
      });
    };

    const accordionBtn = document.querySelectorAll(".faq-head");
    const accordionContent = document.querySelectorAll(".faq-body");
    const accordionWrapper = document.querySelectorAll(".faq-item");

    if (accordionBtn[0])
      accordions(accordionBtn, accordionContent, accordionWrapper);
  }, []);

  return (
    <div className="overflow-hidden bg-gradient-to-b from-[#00060b] to-[#010f1b] min-h-screen">
      <JsonLd />
      <Header />

      <main>
        <Hero />

        {/* ==================== ADVANTAGES ==================== */}
        <section className="mb-20 md:mb-40 lg:mb-52" id="advantages">
          <div className="max-w-[1200px] mx-auto px-6">
            <div className="flex flex-col md:flex-row items-start justify-between gap-8 mb-16 scroll">
              <h2 className="text-white text-3xl md:text-4xl lg:text-[48px] font-medium leading-[1.05] max-w-[670px]">
                Cryptocurrency processing on the best terms
              </h2>
              <p className="text-white/50 text-base leading-[1.5] max-w-[450px]">
                PawPayments accepts various tokens across almost all known
                cryptocurrency networks. We offer the best market conditions for
                partners with long-term business relationships.
              </p>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 relative">
              <div className="absolute top-10 left-0 w-[308px] h-[144px] rounded-full bg-[rgba(0,121,236,0.12)] blur-[70px] -z-10" />
              <div className="absolute -top-10 right-4 w-[618px] h-[428px] rounded-full bg-[rgba(0,121,236,0.1)] blur-[160px] -z-10" />

              {[
                {
                  icon: "/Images/landing/icon-gateway.svg",
                  title: "Licensed gateway",
                  desc: `Registered with FINTRAC as a money services business (MSB ${COMPANY_MSB_REGISTRATION}), built and run entirely in-house`,
                },
                {
                  icon: "/Images/landing/icon-onetouch.svg",
                  title: "One-tap payment",
                  desc: "A unique one-tap payment feature available to your customers",
                },
                {
                  icon: "/Images/landing/icon-settings.svg",
                  title: "Flexible merchant settings",
                  desc: "Manage all coins, customize payment precision, and much more",
                },
                {
                  icon: "/Images/landing/icon-tokens.svg",
                  title: "Wide token selection",
                  desc: "We add both the most popular tokens and networks, as well as emerging ones",
                },
                {
                  icon: "/Images/landing/icon-swap.svg",
                  title: "Auto-conversion",
                  desc: "We automatically convert coins to USDT or USDC. Don't worry about the conversion rate, we'll handle it for you",
                },
                {
                  icon: "/Images/landing/icon-lightning.svg",
                  title: "Instant payouts",
                  desc: "Withdraw received coins to wallets without delays or holds",
                },
                {
                  icon: "/Images/landing/icon-aml.svg",
                  title: "AML protected",
                  desc: "We check all transactions for compliance, so you can be sure that your funds are safe",
                },
              ].map((card, i) => (
                <div
                  key={i}
                  className="scroll backdrop-blur-[17.5px] bg-[rgba(4,4,4,0.35)] border border-[#111] rounded-2xl p-6 flex flex-col gap-4 hover:scale-[1.03] active:scale-[1.03] transition-transform"
                >
                  <div className="w-16 h-16 relative">
                    <img
                      src={card.icon}
                      alt=""
                      width={64}
                      height={64}
                      decoding="async"
                      className="absolute inset-[-20%] w-[140%] h-[140%] object-contain"
                    />
                  </div>
                  <h3 className="text-white text-lg font-medium">
                    {card.title}
                  </h3>
                  <p className="text-white/35 text-xs leading-normal">
                    {card.desc}
                  </p>
                </div>
              ))}

              <div className="scroll backdrop-blur-[17.5px] bg-[rgba(4,4,4,0.35)] border border-[#0079ec] rounded-2xl p-6 flex flex-col items-center justify-center gap-3 -rotate-[2deg] hover:rotate-0 transition-transform">
                <div className="w-full h-[69px] flex items-center justify-center">
                  <img
                    src="/Images/advantages/icons.svg"
                    alt=""
                    width={167}
                    height={105}
                    decoding="async"
                    className="h-[69px] w-auto max-w-full object-contain"
                  />
                </div>
                <span className="text-white text-lg font-medium text-center">
                  Ready to start?
                </span>
                <a
                  href="/auth"
                  className="flex items-center gap-1 text-[#0079ec] text-xs font-medium"
                >
                  Connect now
                  <svg width="14" height="14" viewBox="0 0 20 19" fill="none">
                    <path
                      d="M13.149 3.717L11.87 5.096 14.834 8.261 2.5 8.274 2.502 10.218 14.803 10.206 11.888 13.346 13.173 14.717 18.29 9.204z"
                      fill="#0079ec"
                    />
                  </svg>
                </a>
              </div>
            </div>
          </div>
        </section>

        {/* ==================== CONDITIONS ==================== */}
        <section className="mb-20 md:mb-40 lg:mb-64" id="conditions">
          <div className="max-w-[1200px] mx-auto px-6">
            <div className="flex flex-col lg:flex-row items-center justify-between gap-8">
              <div className="max-w-[568px] w-full flex flex-col gap-10 relative scroll">
                <div className="absolute -top-[220px] -left-[260px] w-[622px] h-[622px] rounded-full bg-[rgba(0,121,236,0.1)] blur-[150px] -z-10" />

                <div className="flex flex-col gap-6">
                  <h2 className="text-white text-3xl md:text-4xl lg:text-[48px] font-medium leading-[1.05]">
                    Transparent rates
                    <br />
                    with no hidden fees
                  </h2>
                  <p className="text-white/50 text-lg leading-[1.3]">
                    We offer the best conditions for accepting digital assets on
                    the market for partners with long-term business perspectives
                  </p>
                </div>

                <div className="flex flex-wrap gap-6">
                  <div className="w-[135px] pr-2.5 border-r border-white/10 flex flex-col gap-2">
                    <span className="text-white text-5xl">1.8%</span>
                    <span className="text-white/35 text-sm leading-[1.2]">
                      For small
                      <br />
                      companies
                    </span>
                  </div>
                  <div className="w-[135px] pr-2.5 border-r border-white/10 flex flex-col gap-2">
                    <span className="text-white text-5xl">1.2%</span>
                    <span className="text-white/35 text-sm leading-[1.2]">
                      For medium
                      <br />
                      business
                    </span>
                  </div>
                  <div className="w-[194px] flex flex-col gap-2 justify-center">
                    <span className="text-white text-5xl">from 0.5%</span>
                    <span className="text-white/35 text-sm leading-[1.2]">
                      Custom terms for
                      <br />
                      high-load projects
                    </span>
                  </div>
                </div>

                <a
                  href="/auth"
                  className="inline-flex items-center gap-2.5 bg-[#0079ec] text-white text-lg font-medium px-7 py-4 rounded-xl self-start shadow-[0_0_48px_rgba(0,121,236,0.5)] hover:bg-[#0069d0] transition-colors"
                >
                  Discuss terms
                  <svg width="20" height="19" viewBox="0 0 20 19" fill="none">
                    <path
                      d="M13.149 3.717L11.87 5.096 14.834 8.261 2.5 8.274 2.502 10.218 14.803 10.206 11.888 13.346 13.173 14.717 18.29 9.204z"
                      fill="white"
                    />
                  </svg>
                </a>
              </div>

              <div className="max-w-[568px] w-full scroll">
                <picture>
                  <source srcSet="/Images/landing/conditions-coins.webp" type="image/webp" />
                  <img
                    src="/Images/landing/conditions-coins.png"
                    alt=""
                    width={568}
                    height={568}
                    loading="eager"
                    fetchPriority="high"
                    decoding="async"
                    className="w-full"
                  />
                </picture>
              </div>
            </div>
          </div>
        </section>

        {/* ==================== CRYPTO METHODS ==================== */}
        <section className="mb-20 md:mb-40 lg:mb-64" id="methods">
          <div className="max-w-[1448px] mx-auto overflow-hidden">
            <div className="flex flex-col items-center gap-6 max-w-[960px] mx-auto mb-14 px-6 scroll">
              <h2 className="text-white text-3xl md:text-4xl lg:text-[56px] font-semibold leading-[1.05] text-center">
                We accept all popular coins
                <br className="hidden md:block" />
                across various blockchain networks
              </h2>
              <p className="text-white/50 text-base md:text-lg leading-[1.3] text-center max-w-[654px]">
                PawPayments works with both the most popular and emerging tokens
                across various blockchain networks, giving payers a wide choice
              </p>
            </div>

            <div className="relative overflow-hidden">
              <div className="absolute top-0 left-0 w-[300px] md:w-[524px] h-full z-10 bg-gradient-to-r from-[#010f1b] to-transparent pointer-events-none" />
              <div className="absolute top-0 right-0 w-[300px] md:w-[524px] h-full z-10 bg-gradient-to-l from-[#010f1b] to-transparent pointer-events-none" />

              <div className="flex w-max ticker-track animate-[ticker_66s_linear_infinite]">
                {[0, 1].map((setIdx) => (
                  <div key={setIdx} className="flex gap-4 shrink-0 pr-4">
                    {[...METHOD_COINS, ...METHOD_COINS].map((coin, i) => (
                      <div
                        key={i}
                        className="shrink-0 w-[100px] md:w-[150px] h-[100px] md:h-[150px] bg-[#040404] border border-[#111] rounded-2xl backdrop-blur-[17.5px] flex items-center justify-center"
                      >
                        <img
                          src={coin.icon}
                          alt={coin.name}
                          width={80}
                          height={80}
                          decoding="async"
                          className="w-[55px] md:w-[80px] h-[55px] md:h-[80px] object-contain"
                        />
                      </div>
                    ))}
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        <CardPayments />

        <PartnerProgram />

        {/* ==================== FAQ ==================== */}
        <section className="mb-20 md:mb-40" id="faq">
          <div className="max-w-[1200px] mx-auto px-6">
            <h2 className="text-white text-3xl md:text-4xl lg:text-[56px] font-medium leading-[1.05] max-w-[972px] mb-12 scroll">
              Everything you need to know before integrating
            </h2>

            <div className="mb-12 relative scroll">
              <div className="absolute top-[100px] -left-[150px] w-[530px] h-[790px] rounded-full bg-[rgba(0,121,236,0.1)] blur-[150px] -z-10" />

              {FAQ.map((item, i) => (
                <div
                  key={i}
                  className={`faq-item ${i < FAQ.length - 1 ? "border-b border-white/10" : ""}`}
                >
                  <div className="faq-head py-8 md:py-12 cursor-pointer flex items-center justify-between gap-5 group">
                    <h3 className="faq-name text-white text-lg md:text-2xl font-medium leading-[1.05] group-hover:bg-gradient-to-r group-hover:from-[#0079ec] group-hover:to-[#add7ff] group-hover:bg-clip-text group-hover:text-transparent transition-all">
                      {item.q}
                    </h3>
                    <div className="shrink-0 w-[25px] h-[25px] relative">
                      <svg
                        className="faq-plus absolute inset-0 transition-all duration-300"
                        width="25"
                        height="25"
                        viewBox="0 0 25 25"
                        fill="none"
                      >
                        <path
                          d="M12.5 5v15M5 12.5h15"
                          stroke="white"
                          strokeWidth="2"
                        />
                      </svg>
                      <svg
                        className="faq-minus absolute inset-0 opacity-0 transition-all duration-300"
                        width="25"
                        height="25"
                        viewBox="0 0 25 25"
                        fill="none"
                      >
                        <path
                          d="M5 12.5h15"
                          stroke="white"
                          strokeWidth="2"
                        />
                      </svg>
                    </div>
                  </div>
                  <div className="faq-body max-h-0 overflow-hidden transition-all duration-300">
                    <div className="pb-8 md:pb-12 max-w-[1111px]">
                      <p className="text-white/50 text-base md:text-lg leading-[1.4]">
                        {item.a}
                      </p>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            <p className="text-white/50 text-base scroll">
              If your question isn&apos;t answered here, please contact{" "}
              <Link href="/#contacts" className="text-[#0079ec] underline">
                technical support
              </Link>
            </p>
          </div>
        </section>

        {/* ==================== OFFER / CTA ==================== */}
        <section className="mb-16 px-6" id="contacts">
          <div
            className="relative max-w-[1872px] mx-auto rounded-[32px] border border-white/10 border-t-0 overflow-hidden py-20 md:py-24"
            style={{
              background: [
                "radial-gradient(ellipse 1000px 400px at 50% -200px, rgba(0,121,236,0.12) 0%, transparent 100%)",
                "radial-gradient(ellipse 700px 200px at 50% -80px, rgba(200,230,255,0.06) 0%, transparent 100%)",
                "linear-gradient(to bottom, #000a14, #000306)",
              ].join(", "),
            }}
          >

            <div className="absolute top-[-21px] right-[50px] w-[471px] h-[471px] mix-blend-plus-lighter hidden lg:block pointer-events-none">
              <picture>
                <source srcSet="/Images/landing/offer-coin-1.webp" type="image/webp" />
                <img
                  src="/Images/landing/offer-coin-1.png"
                  alt=""
                  width={385}
                  height={385}
                  decoding="async"
                  className="w-[385px] rotate-[15deg]"
                />
              </picture>
            </div>
            <div className="absolute bottom-[-100px] right-[200px] w-[375px] h-[375px] opacity-60 hidden lg:block pointer-events-none">
              <picture>
                <source srcSet="/Images/landing/offer-coin-2.webp" type="image/webp" />
                <img
                  src="/Images/landing/offer-coin-2.png"
                  alt=""
                  width={268}
                  height={268}
                  decoding="async"
                  className="w-[268px] -rotate-[53deg]"
                />
              </picture>
            </div>
            <div className="absolute bottom-[-50px] left-[200px] w-[317px] h-[317px] opacity-60 hidden lg:block pointer-events-none">
              <picture>
                <source srcSet="/Images/landing/offer-coin-3.webp" type="image/webp" />
                <img
                  src="/Images/landing/offer-coin-3.png"
                  alt=""
                  width={282}
                  height={282}
                  decoding="async"
                  className="w-[282px] rotate-[8deg]"
                />
              </picture>
            </div>
            <div className="absolute top-[-23px] left-[50px] w-[410px] h-[410px] mix-blend-plus-lighter hidden lg:block pointer-events-none">
              <picture>
                <source srcSet="/Images/landing/offer-coin-4.webp" type="image/webp" />
                <img
                  src="/Images/landing/offer-coin-4.png"
                  alt=""
                  width={361}
                  height={361}
                  decoding="async"
                  className="w-[361px] rotate-[172deg] -scale-y-100"
                />
              </picture>
            </div>

            <div className="relative z-10 flex flex-col items-center gap-8 max-w-[1006px] mx-auto px-6 text-center">
              <div className="flex flex-col gap-6">
                <h2 className="text-white text-3xl md:text-4xl lg:text-[56px] font-medium leading-[1.05] text-balance">
                  <span className="block">Every customer can pay.</span>
                  <span className="block">You get stablecoins.</span>
                </h2>
                <p className="text-[#7e9ab5] text-base md:text-lg max-w-[520px] mx-auto">
                  30+ coins for those who hold crypto, a card for those who don&apos;t. Fees from 0.5%.
                </p>
              </div>

              <div className="flex flex-col items-center gap-4">
                <a
                  href="/auth"
                  className="bg-[#0079ec] text-white text-lg font-medium px-7 py-5 rounded-xl shadow-[0_0_48px_rgba(0,121,236,0.5)] flex items-center gap-3 hover:bg-[#0069d0] transition-colors"
                >
                  Start accepting payments
                  <svg width="20" height="19" viewBox="0 0 20 19" fill="none">
                    <path
                      d="M13.149 3.717L11.87 5.096 14.834 8.261 2.5 8.274 2.502 10.218 14.803 10.206 11.888 13.346 13.173 14.717 18.29 9.204z"
                      fill="white"
                    />
                  </svg>
                </a>
                <div className="flex items-center gap-2">
                  <img
                    src="/Images/landing/icon-clock.svg"
                    alt=""
                    width={20}
                    height={20}
                    decoding="async"
                    className="w-5 h-5"
                  />
                  <span className="text-white text-base font-medium">
                    Setup in just 5 minutes
                  </span>
                </div>
              </div>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
}
